/**
 * Authentication utility functions
 * Handles token storage, validation, and refresh
 */

import { getApiBaseUrl } from '../api/apiConfig';

const STORAGE_KEY = 'edupravahaa_admin_user';

/**
 * Decode JWT payload (base64url-safe; supports UTF-8 claims).
 * @param {string} token
 * @returns {Object|null}
 */
export function decodeJwtPayload(token) {
  if (!token || typeof token !== 'string') return null;
  const parts = token.split('.');
  if (parts.length !== 3) return null;
  let base64 = parts[1].replace(/-/g, '+').replace(/_/g, '/');
  const pad = base64.length % 4;
  if (pad) base64 += '='.repeat(4 - pad);
  try {
    const binary = atob(base64);
    const bytes = new Uint8Array(binary.length);
    for (let i = 0; i < binary.length; i += 1) {
      bytes[i] = binary.charCodeAt(i);
    }
    const json = new TextDecoder().decode(bytes);
    return JSON.parse(json);
  } catch {
    return null;
  }
}

/**
 * Get stored authentication data from localStorage
 * @returns {Object|null} Auth data object with {admin, access, refresh} or null
 */
export function getStoredAuth() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    return JSON.parse(raw);
  } catch (error) {
    console.error('Error reading auth from localStorage:', error);
    localStorage.removeItem(STORAGE_KEY);
    return null;
  }
}

/**
 * Store authentication data in localStorage
 * @param {Object} authData - Auth data object with {admin, access, refresh}
 */
export function storeAuth(authData) {
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(authData));
  } catch (error) {
    console.error('Error storing auth in localStorage:', error);
  }
}

/**
 * Remove authentication data from localStorage
 */
export function clearAuth() {
  try {
    localStorage.removeItem(STORAGE_KEY);
  } catch (error) {
    console.error('Error clearing auth from localStorage:', error);
  }
}

/**
 * Get the access token from stored auth
 * @returns {string|null} Access token or null
 */
export function getAccessToken() {
  const auth = getStoredAuth();
  return auth?.access || null;
}

/**
 * Get the refresh token from stored auth
 * @returns {string|null} Refresh token or null
 */
export function getRefreshToken() {
  const auth = getStoredAuth();
  return auth?.refresh || null;
}

/**
 * Check if user is authenticated (has valid token)
 * @returns {boolean}
 */
export function isAuthenticated() {
  const token = getAccessToken();
  return !!token && !isTokenExpired(token);
}

/**
 * Check if a JWT token is expired
 * @param {string} token - JWT token string
 * @returns {boolean} True if expired or invalid
 */
export function isTokenExpired(token) {
  if (!token) return true;

  const payload = decodeJwtPayload(token);
  if (!payload) return true;

  if (payload.exp && payload.exp * 1000 < Date.now()) {
    return true;
  }

  return false;
}

/**
 * Get token expiration time
 * @param {string} token - JWT token string
 * @returns {Date|null} Expiration date or null if invalid
 */
export function getTokenExpiration(token) {
  if (!token) return null;

  const payload = decodeJwtPayload(token);
  if (payload?.exp) {
    return new Date(payload.exp * 1000);
  }
  return null;
}

/**
 * Refresh the access token using refresh token
 * @param {string} refreshToken - Refresh token string
 * @returns {Promise<Object>} New auth data with {admin, access, refresh}
 */
export async function refreshAccessToken(refreshToken) {
  if (!refreshToken) {
    throw new Error('No refresh token available');
  }

  const baseURL = getApiBaseUrl();
  
  // Try standard SimpleJWT refresh endpoint first
  const refreshEndpoints = [
    '/api/token/refresh/',
    '/api/auth/token/refresh/',
    '/api/auth/admin-login/refresh/', // Custom endpoint if exists
  ];

  let lastError = null;

  for (const endpoint of refreshEndpoints) {
    try {
      const response = await fetch(`${baseURL}${endpoint}`, {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({ refresh: refreshToken }),
      });

      if (response.ok) {
        const data = await response.json();
        
        // Get existing auth data to preserve admin info
        const existingAuth = getStoredAuth();
        
        return {
          admin: existingAuth?.admin || null,
          access: data.access,
          refresh: data.refresh || refreshToken, // Use new refresh token if provided
        };
      }
      
      // If 404, try next endpoint
      if (response.status === 404) {
        continue;
      }
      
      // Other errors, throw
      const errorData = await response.json().catch(() => ({}));
      throw new Error(errorData.detail || 'Token refresh failed');
    } catch (error) {
      lastError = error;
      // If it's a network error or 404, try next endpoint
      if (error.message.includes('fetch') || error.message.includes('404')) {
        continue;
      }
      // Otherwise, throw immediately
      throw error;
    }
  }

  // If all endpoints failed, throw the last error
  console.error('All token refresh endpoints failed:', lastError);
  throw lastError || new Error('Token refresh failed - no endpoint available');
}

/**
 * Validate and refresh token if needed
 * @returns {Promise<string|null>} Valid access token or null
 */
export async function ensureValidToken() {
  const token = getAccessToken();
  
  if (!token) {
    return null;
  }

  // If token is not expired, return it
  if (!isTokenExpired(token)) {
    return token;
  }

  // Token is expired, try to refresh
  const refreshToken = getRefreshToken();
  if (!refreshToken) {
    clearAuth();
    return null;
  }

  try {
    const newAuth = await refreshAccessToken(refreshToken);
    storeAuth(newAuth);
    return newAuth.access;
  } catch (error) {
    // Refresh failed, clear auth
    clearAuth();
    return null;
  }
}

/**
 * Get user info from stored auth
 * @returns {Object|null} User/admin object or null
 */
export function getUser() {
  const auth = getStoredAuth();
  return auth?.admin || null;
}

/**
 * Check if current user has a specific role
 * @param {string} role - Role to check
 * @returns {boolean}
 */
export function hasRole(role) {
  const user = getUser();
  return user?.role === role;
}

/**
 * Logout helper - clears auth and redirects
 * @param {Function} navigate - React Router navigate function
 */
export function logout(navigate) {
  clearAuth();
  if (navigate) {
    navigate('/login', { replace: true });
  }
}

